import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Card,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Grid,
  Stack
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { getJobOrdersByClientId } from "../API/clientAPI";

function ClientJobOrderHistory() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jobOrders, setJobOrders] = useState([]);
  const [error, setError] = useState(null);
  const [clientName, setClientName] = useState(""); // Add this state
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // Map form types to their corresponding route segments
  const formTypeToSegment = {
    makati_gpc_form: "makati-gpc-form",
    qc_form: "qc-form",
    termite_control_form: "termite-control-form",
    transmittal_form: "transmittal-form",
  };

  // Decide which segment to use for a given order
  const getSegment = (order) => {
    const isUpcomingTab = tab === 1;

    // UPCOMING JOB ORDERS always use job-order
    if (isUpcomingTab) return "job-order";

    // PAST JOB ORDERS use form_type mapping (fallback to job-order)
    return formTypeToSegment[order.form_type] || "job-order";
  };

  // Common returnTo for both view/edit routes
  const getReturnTo = () => encodeURIComponent(`/job-order-history/${clientId}`);

  // Build route to the correct view page for a given job order
  const getViewReportRoute = (order) => {
    const segment = getSegment(order);
    const returnTo = getReturnTo();
    return `/view-${segment}/${order.id}?returnTo=${returnTo}`;
  };

  // Build route to the correct edit page for a given job order
  const getEditReportRoute = (order) => {
    const segment = getSegment(order);
    const returnTo = getReturnTo();
    return `/edit-${segment}/${order.id}?returnTo=${returnTo}`;
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    (async () => {
      try {
        const { orders, clientName } = await getJobOrdersByClientId(clientId);
        if (!isMounted) return;
        setJobOrders(orders);
        setClientName(clientName);
        setError(null);
      } catch {
        if (!isMounted) return;
        setError("Failed to fetch job orders.");
        setJobOrders([]);
        setClientName("");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [clientId]);

  // Separate job orders by date
  const now = new Date();
  const ordersArray = Array.isArray(jobOrders) ? jobOrders : [];
  const pastOrders = ordersArray
    .filter(order => new Date(order.job_date) < now)
    .sort((a, b) => new Date(b.job_date) - new Date(a.job_date));
  const upcomingOrders = ordersArray
    .filter(order => new Date(order.job_date) >= now)
    .sort((a, b) => new Date(a.job_date) - new Date(b.job_date));

  // Table columns: Job Date, Description, Status, Actions
  const renderTable = (orders) => {
    const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return (
      <Card variant="outlined" sx={{ padding: "20px", boxShadow: 0, borderRadius: 1 }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {[
                  "Job Date",
                  "Start Time",
                  "End Time",
                  "Job Type",
                  "Status",
                  "Reports",
                  "Receipts",
                  "Certificates",
                  "Actions"
                ].map((header) => (
                  // <TableCell
                  //   key={header}
                  //   sx={{
                  //     fontSize: "0.8rem",
                  //     fontWeight: "bold",
                  //     padding: "6px",
                  //     backgroundColor: "#f7f7f7"
                  //   }}
                  // >
                  //   {header}
                  // </TableCell>
                  <TableCell
                    key={header}
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      padding: "6px",
                      backgroundColor: "#f7f7f7",
                      ...(header === "Actions"
                        ? { textAlign: "center", width: 170, whiteSpace: "nowrap" }
                        : {})
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map(order => (
                <TableRow key={order.id}>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.job_date || ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.start_time || ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.end_time || ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.job_types && order.job_types.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {order.job_types.map((type, i) => (
                          <Chip
                            key={i}
                            label={type.replace(/_/g, " ")}
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              height: "24px"
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.status || "-"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.reports_url ? (
                      <a
                        href={order.reports_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                        }}
                      >
                        View Report
                      </a>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.receipts_url ? (
                      <a
                        href={order.receipts_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                        }}
                      >
                        View Receipt
                      </a>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                    {order.certificate_url ? (
                      <a
                        href={order.certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                        }}
                      >
                        View Certificate
                      </a>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      padding: "6px",
                      textAlign: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ minHeight: 30 }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        component="a"
                        href={getViewReportRoute(order)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          minWidth: 68,
                          height: 26,
                          textTransform: "none",
                          borderRadius: 1
                        }}
                      >
                        View
                      </Button>

                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        component="a"
                        href={getEditReportRoute(order)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: "0.72rem",
                          padding: "3px 10px",
                          minWidth: 68,
                          height: 26,
                          textTransform: "none",
                          borderRadius: 1
                        }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={orders.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
        />
      </Card>
    );
  };

  // Reset page when tab changes or orders change
  useEffect(() => {
    setPage(0);
  }, [tab, jobOrders]);

  return (
    <BaseWrapper
      titleIcon={null}
      title="Manage Clients"
      subTitle="Job Order History"
    >
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ p: 0 }}>
            {/* Breadcrumb and client info above the table */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                {clientName || "Client"}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontSize: "0.70rem" }}>
                Client ID: {clientId}
              </Typography>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="job order tabs">
                <Tab label="Past Job Orders" />
                <Tab label="Upcoming Job Orders" />
              </Tabs>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : tab === 0 ? (
              pastOrders.length > 0 ? (
                renderTable(pastOrders)
              ) : (
                <Typography>No past job orders found.</Typography>
              )
            ) : (
              upcomingOrders.length > 0 ? (
                renderTable(upcomingOrders)
              ) : (
                <Typography>No upcoming job orders found.</Typography>
              )
            )}
            {/* Always show Back button at lower right */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/manage-clients')}
                sx={{ minWidth: 100 }}
              >
                Back
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </BaseWrapper>
  );
}

export default ClientJobOrderHistory;


